import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { LOGIN_ROUTER } from 'constant/router'
import { Mixpanel } from 'Utils/Mixpanel'
import { useForm } from 'react-hook-form'
import { Logo } from 'Organisms'
import { emailValidate } from 'Utils/helperFunctions'
import { DateTime } from 'luxon'
import { useSignUp } from 'Hooks'
import { ModalSuccess } from 'Organisms/ModalSuccess'
import { WarningCircle } from '@phosphor-icons/react'
import { GoogleBrandIcon } from 'Organisms/Svg'
import { Helmet } from 'react-helmet'
import { trackEvent } from 'Utils/GoogleAnalytics'
import { signUpUsingProvider } from 'Hooks/useAuth'
import { PENDING_COMPANY_STORAGE } from 'Utils/const/localstorage'

// ? Must match the action verified server-side (SignupService.create -> verifyRecaptchaToken) -
// v3 ties a token to the specific action it was requested for, so a mismatch is treated as abuse.
const RECAPTCHA_SIGNUP_ACTION = 'signup'

// ? v3 is invisible (no widget to mount/ref) - a token is requested fresh at submit time instead
// of tracked in state. grecaptcha.ready() guards against the script (loaded in public/index.html)
// not being parsed yet on a very fast first submit.
const getRecaptchaToken = () =>
  new Promise((resolve, reject) => {
    if (!window.grecaptcha) {
      return reject(new Error('reCAPTCHA failed to load'))
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, { action: RECAPTCHA_SIGNUP_ACTION })
        .then(resolve)
        .catch(reject)
    })
  })

const Signup = () => {
  const [loading, setLoading] = useState(false)
  const [modalMessage, setModalMessage] = useState({
    open: false
  })

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors }
  } = useForm()

  const { signUpHook } = useSignUp()

  const navigate = useNavigate()

  const onSubmit = async (data) => {
    try {
      if (!emailValidate({ email: data.email })) {
        return setError('email', { type: 'format', message: 'Format is invalid' })
      }

      let recaptchaToken

      try {
        recaptchaToken = await getRecaptchaToken()
      } catch {
        return setModalMessage({
          open: true,
          error: true,
          message: 'We couldn’t verify you’re not a robot. Please refresh the page and try again.'
        })
      }

      setLoading(true)

      const split_name = data.full_name.split(' ')

      const timezone = `GMT${DateTime.local().toFormat('ZZ')}`
      Mixpanel.track('Sign up Intent')
      const res = await signUpHook({ ...data, timezone, recaptchaToken, firstname: split_name.shift(), lastname: split_name.join(' ') })

      setModalMessage({
        open: true,
        title: res.success
          ? (
          <span>
            You are almost done! <br /> Now, verify your email
          </span>
            )
          : (
              ''
            ),
        message: res.success
          ? (
          <p className="m-0 text-center">
            {"You're"} in. An email has been sent to <br />
            <span className="text-quaternary">{data.email}</span>
            <br /> Hit confirm and {"you'll"} be ready to start working.
          </p>
            )
          : (
              res?.message || 'Oops! Something went wrong while creating your account. Please try again later.'
            ),
        error: !res.success
      })

      if (res.success) {
        Mixpanel.track('Signed up')
        Mixpanel.identify(res.data)
        Mixpanel.people.set({
          $distinct_id: res.data,
          $email: data.email,
          $name: split_name.shift() + ' ' + split_name.join(' '),
          $first_name: split_name.shift(),
          $last_name: split_name.join(' ')
        })

        trackEvent({
          category: 'User',
          action: 'Sign up',
          label: ''
        })
      } else {
        Mixpanel.track('Signed up Failed')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCloseModal = () => {
    if (modalMessage.error) return setModalMessage({ open: false, message: '' })

    return navigate(LOGIN_ROUTER)
  }

  const onGoogleSignUp = () => {
    const company = (getValues('company') || '').trim()

    if (company) {
      sessionStorage.setItem(PENDING_COMPANY_STORAGE, company)
    } else {
      sessionStorage.removeItem(PENDING_COMPANY_STORAGE)
    }

    Mixpanel.track('Sign up Intent')
    signUpUsingProvider('Google')
  }

  return (
    <>
      <Helmet>
        <title>Sign up</title>
        <meta
          name="description"
          content="Time and task Tracking. Free trial available. Three pricing plans available."
        />
        <meta
          name="keywords"
          content="time tracker, time tracking, remote employees, productivity, software, remote work"
        />
        <meta name="author" content="Canned Head" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Sign Up" />
        <meta
          property="og:description"
          content="Time and task Tracking. Free trial available. Three pricing plans available."
        />
        <meta property="og:image" content="https://example.com/image.jpg" />
        <meta property="og:url" content="/signup" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="container login dark-theme">
        <div className="row justify-content-center align-items-center">
          <div className="col-11 col-md-8 col-lg-5">
            <div className="row justify-content-center align-items-center">
              <div className="col-12 col-md-8">
                <div className="row justify-content-center">
                  <div className="col-9 logo-wrapper">
                    <Logo />
                  </div>
                </div>

                <p className="mt-5">Let&apos;s get started! Create an account for free</p>

                <div className="row justify-content-center">
                  <form id="formSignUp" className="col-12 d-grid" onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="inputCompany">
                        Company / Organization
                      </label>
                      <div className="col-12">
                        <input
                          type="text"
                          {...register('company', { required: true })}
                          className={`form-control form-control-outline ${
                            errors && errors.company ? 'is-invalid' : ''
                          }`}
                          id="inputCompany"
                          placeholder="Enter company name"
                        />
                        <div className="invalid-feedback">Please provide a company name.</div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label" htmlFor="inputFullName">
                        Full name
                      </label>
                      <div className="col-12">
                        <input
                          type="text"
                          {...register('full_name', { required: true })}
                          className={`form-control form-control-outline ${
                            errors && errors.full_name ? 'is-invalid' : ''
                          }`}
                          id="inputFullName"
                          placeholder="Enter your First name"
                        />
                        <div className="invalid-feedback">Please provide your full name.</div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label" htmlFor="inputEmail">
                        Email
                      </label>
                      <div className="col-12">
                        <input
                          type="text"
                          {...register('email', { required: true })}
                          className={`form-control form-control-outline ${errors && errors.email ? 'is-invalid' : ''}`}
                          id="inputEmail"
                          placeholder="Enter your email"
                        />

                        <div className="invalid-feedback">
                          {errors?.email?.message ? errors.email.message : 'Please provide your email.'}
                        </div>
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="btn btn-block btn-primary mt-4"
                      disabled={loading}
                      form="formSignUp">
                      {loading && (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          <span className="ms-2">Loading...</span>
                        </>
                      )}
                      {!loading && <span>Sign up</span>}
                    </button>
                  </form>
                  <p className="mt-3 text-center small">
                    By clicking &quot;Sign up&quot;, you agree to Pulse{' '}
                    <Link className="text-active" to="/terms-of-service">
                      TOS
                    </Link>{' '}
                    and{' '}
                    <Link className="text-active" to="/privacy-policy">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                  <p className="mt-2 text-center small">
                    This site is protected by reCAPTCHA and the Google{' '}
                    <a className="text-active" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a className="text-active" href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
                      Terms of Service
                    </a>{' '}
                    apply.
                  </p>

                  <div className="col-12 mt-2">
                    <div className="social-divider text-center">
                      <span className="or">or</span>
                    </div>
                  </div>
                  <div className="col-12 d-grid mt-3">
                    <button
                      type="button"
                      className="btn btn-block btn-google"
                      onClick={onGoogleSignUp}
                      disabled={loading}>
                      <GoogleBrandIcon className="google-button__icon" size={18} />
                      <span className="google-button__text">Continue with Google</span>
                    </button>
                  </div>
                </div>
                <div className="row mt-5">
                  <div className="col-12 text-center">
                    <p>
                      Already have an account?{' '}
                      <Link className="forgot-password" to={LOGIN_ROUTER}>
                        Log in
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {modalMessage.open && (
        <ModalSuccess
          visible={true}
          width={400}
          setVisible={() => handleCloseModal()}
          title={modalMessage.error ? modalMessage.message : ''}
          content={
            !modalMessage.error
              ? (
              <>
                <h1 className="fs-3 text-center">{modalMessage.title}</h1>
                <p className="m-0">{modalMessage.message}</p>
              </>
                )
              : (
                  ''
                )
          }
          identifier="modalMessageSuccess"
          error={modalMessage.error}
          icon={modalMessage.error && WarningCircle}
        />
      )}
    </>
  )
}

export default Signup
